// create.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useModal } from '../../../components/layout';
import { useProfileManagement } from '../../../components/auth/useProfileManagement';
import ApiSourceForm from '../../../components/data-sources/api-sources/ApiSourceForm';
import { API_ENDPOINTS } from '../../../config/api.config';
import { apiService } from '../../../services/apiService';
import { ApiSourceFormData } from '../../../types';
import { transformFormDataToApiPayload } from "../../../utils/apiSourceUtils";
import ContentSkeleton from '../../../components/loading/ContentSkeleton';

const CreateAPISource: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const modalState = useModal();
    const [isLoading, setIsLoading] = useState(true);

    useProfileManagement(
        undefined,
        modalState.setError,
        modalState.setSuccess,
        modalState.setIsLoading,
        modalState.setShowProfileModal,
        modalState.setShowPasswordModal
    );

    // Simulate brief loading for consistency
    React.useEffect(() => {
        const timer = setTimeout(() => setIsLoading(false), 100);
        return () => clearTimeout(timer);
    }, []);

    const handleSubmit = async (formData: ApiSourceFormData) => {
        modalState.setIsLoading(true);
        modalState.setError(null);

        try {
            const transformedData = transformFormDataToApiPayload(formData);
            const apiData = {
                ...transformedData,
                name: formData.name,
                description: formData.description || '',
                source_type: 'api_query'
            };

            console.log('Sending API data:', apiData);

            await apiService.post(API_ENDPOINTS.DATA_SOURCE.API_SOURCES.CREATE, apiData);

            modalState.setSuccess('API source created successfully');
            navigate('/data-sources/api');
        } catch (err) {
            console.error('Error creating API source:', err);
            modalState.setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/data-sources/api');
    };

    if (!user) return null;

    return (
        <div className="page-container">
            <div className="content-header">
                <h5 className="mb-4 fw-semibold">New API Source</h5>
                {isLoading ? (
                    <ContentSkeleton />
                ) : (
                    <ApiSourceForm
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        error={modalState.error}
                        isLoading={modalState.isLoading}
                        isEdit={false}
                    />
                )}
            </div>
        </div>
    );
};

export default CreateAPISource;